import { useRef, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { PexIcon } from '../../../components/icons/PexIcon'
import { cn } from '../../../lib/utils'

type MessagingShellProps = {
  open: boolean
  expanded: boolean
  title: string
  subtitle: string
  showBack: boolean
  onBack: () => void
  onExpandedChange: (expanded: boolean) => void
  onClose: () => void
  children: ReactNode
  backLabel?: string
  expandLabel?: string
  collapseLabel?: string
  closeLabel?: string
}

const controlClassName =
  'inline-flex min-h-10 min-w-10 items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#12161d]'

export function MessagingShell({
  open,
  expanded,
  title,
  subtitle,
  showBack,
  onBack,
  onExpandedChange,
  onClose,
  children,
  backLabel = 'Back to conversations',
  expandLabel = 'Expand messaging',
  collapseLabel = 'Collapse messaging',
  closeLabel = 'Close messaging',
}: MessagingShellProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose()
  }

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    const focusedElement = document.activeElement
    if (!(focusedElement instanceof HTMLElement)) return
    const composer = focusedElement.closest(
      '[data-messaging-media-panel-open="true"]',
    )
    if (composer && contentRef.current?.contains(composer)) {
      event.preventDefault()
    }
  }

  return (
    <DialogPrimitive.Root
      open={open}
      modal={false}
      onOpenChange={handleOpenChange}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          onEscapeKeyDown={handleEscapeKeyDown}
          data-layout={expanded ? 'expanded' : 'compact'}
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden border border-white/10 bg-[#12161d] text-white shadow-[0_24px_70px_rgba(2,8,23,0.34)] outline-none',
            'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=open]:slide-in-from-bottom-3 motion-safe:data-[state=open]:zoom-in-95',
            'motion-safe:data-[state=closed]:animate-out motion-safe:data-[state=closed]:fade-out-0 motion-safe:data-[state=closed]:slide-out-to-bottom-3 motion-safe:data-[state=closed]:zoom-out-95',
            'motion-reduce:transition-none',
            'max-sm:inset-x-2 max-sm:top-[max(0.5rem,env(safe-area-inset-top))] max-sm:bottom-[max(0.5rem,env(safe-area-inset-bottom))] max-sm:h-auto max-sm:rounded-[22px]',
            expanded
              ? 'sm:bottom-6 sm:right-6 sm:h-[min(760px,calc(100dvh-48px))] sm:w-[min(960px,calc(100vw-48px))] sm:rounded-[24px]'
              : 'sm:bottom-6 sm:right-6 sm:h-[min(700px,calc(100dvh-32px))] sm:w-[min(400px,calc(100vw-24px))] sm:rounded-[24px]',
          )}
        >
          <header className="flex min-h-[72px] shrink-0 items-center gap-2 border-b border-white/10 px-3 sm:px-4">
            {showBack ? (
              <button
                type="button"
                aria-label={backLabel}
                className={controlClassName}
                onClick={onBack}
              >
                <PexIcon
                  name="caretLeft"
                  size="sm"
                  tone="on-emphasis"
                  decorative
                />
              </button>
            ) : (
              <span
                aria-hidden="true"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[#12161d]"
              >
                <PexIcon name="messages" size="sm" decorative />
              </span>
            )}

            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-[15px] font-semibold tracking-[-0.01em] text-white">
                {title}
              </DialogPrimitive.Title>
              <p className="mt-0.5 truncate text-xs text-white/55">{subtitle}</p>
            </div>

            <button
              type="button"
              aria-label={expanded ? collapseLabel : expandLabel}
              className={cn(controlClassName, 'hidden sm:inline-flex')}
              onClick={() => onExpandedChange(!expanded)}
            >
              <PexIcon
                name={expanded ? 'decrease' : 'externalLink'}
                size="sm"
                tone="on-emphasis"
                decorative
              />
            </button>

            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label={closeLabel}
                className={controlClassName}
              >
                <PexIcon
                  name="close"
                  size="sm"
                  tone="on-emphasis"
                  decorative
                />
              </button>
            </DialogPrimitive.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
